import { useRouter } from 'next/router'
import { any, intersection, uniq } from 'ramda'
import { useEffect, useState } from 'react'
import { createClient } from 'services/prismic'

import { isPresent } from 'utils'

import prismicArtisanToArtisanCardProps from 'converters/prismicArtisanToArtisanCardProps'

import ArtisanCard from 'components/ArtisanCard'
import Divider from 'components/Divider'
import PrismicPage from 'components/PrismicPage'
import RadioInputs from 'components/RadioInputs'
import SmallScreenModal from 'components/SmallScreenModal'
import Spacer from 'components/Spacer'
import SubHeader from 'components/SubHeader'
import Text from 'components/Text'

import { Artisans, Column, Columns, Filter, Main } from './index.style'

const customAndBespokeOfferings = ['Bespoke', 'Made-to-Measure', 'Made-to-Order']

const categoryOptions = [
  { label: 'All', value: '' },
  { label: 'Suits & Sport Coats', value: 'Suits & Sport Coats' },
  { label: 'Shirts', value: 'Shirts' },
  { label: 'Trousers', value: 'Trousers' },
  { label: 'Shoes', value: 'Shoes' },
]

const offeringsOptions = [
  { label: 'All', value: '' },
  { label: 'Ready-to-Wear', value: 'Ready-to-Wear' },
  { label: 'Custom & Bespoke', value: 'Custom & Bespoke' },
]

const defaultCountryOptions = [{ label: 'All', value: '' }]

export default function Index({ document, artisans, countryOptions }) {
  const router = useRouter()

  const [categoryOption, setCategoryOption] = useState(router.query.category || '')
  const [offeringsOption, setOfferingsOption] = useState(router.query.offerings || '')
  const [countryOption, setCountryOption] = useState(router.query.country || '')

  const handleCategoryChange = (value) => setCategoryOption(value)
  const handleOfferingsChange = (value) => setOfferingsOption(value)
  const handleCountryChange = (value) => setCountryOption(value)

  // const reset = () => {
  //   setCategoryOption(null)
  //   setLocationOption(null)
  // }

  useEffect(() => {
    const query = {}

    if (isPresent(categoryOption)) query.category = categoryOption
    if (isPresent(offeringsOption)) query.offerings = offeringsOption
    if (isPresent(countryOption)) query.country = countryOption

    router.push({
      pathname: '/artisans',
      query,
    })
  }, [categoryOption, offeringsOption, countryOption])

  return (
    <>
      <Spacer y="insetY" />
      <PrismicPage document={document} />
      <Spacer y="insetY" />
      <Main>
        <Filter>
          <SmallScreenModal toggleText="Filter">
            <SubHeader>Filter</SubHeader>
            <Spacer y={2} />
            <Text weight="bold">Category</Text>
            <Spacer y={1} />
            <RadioInputs options={categoryOptions} value={categoryOption} onChange={handleCategoryChange} />
            <Spacer y={4} />
            <Text weight="bold">Offerings</Text>
            <Spacer y={1} />
            <RadioInputs options={offeringsOptions} value={offeringsOption} onChange={handleOfferingsChange} />
            <Spacer y={4} />
            <Text weight="bold">Country</Text>
            <Spacer y={1} />
            <RadioInputs options={countryOptions} value={countryOption} onChange={handleCountryChange} />
          </SmallScreenModal>
        </Filter>
        <Artisans>
          <Columns>
            {artisans.map(({ key, ...props }) => (
              <Column key={key}>
                <ArtisanCard {...props} />
              </Column>
            ))}
          </Columns>
        </Artisans>
      </Main>
      <Spacer y={16} />
      <Divider />
    </>
  )
}

export async function getServerSideProps({ previewData, query }) {
  const category = query.category
  const offerings = query.offerings
  const country = query.country

  const filtered = any(isPresent, [category, offerings, country])

  const prismic = createClient({ previewData })
  const document = await prismic.getPage('artisans')

  let artisans = await prismic.getAllByType('artisan', {
    orderings: {
      field: 'my.artisan.name',
      direction: 'asc',
    },
  })

  if (offerings && offerings === 'Ready-to-Wear') {
    artisans = artisans.filter(({ data: { custom_offerings } }) => {
      const customOfferings = custom_offerings.map(({ custom_offering }) => custom_offering)
      return customOfferings.includes('Ready-to-Wear')
    })
  }

  if (offerings && offerings === 'Custom & Bespoke') {
    artisans = artisans.filter(({ data: { custom_offerings } }) => {
      const artisanCustomOfferings = custom_offerings.map(({ custom_offering }) => custom_offering)
      return intersection(customAndBespokeOfferings, artisanCustomOfferings).length > 0
    })
  }

  if (category) {
    artisans = artisans.filter(({ data: { categories } }) => {
      const artisanCategories = categories.map(({ custom_offering }) => custom_offering)
      return artisanCategories.includes(category)
    })
  }

  const countryOptions = [...defaultCountryOptions]
  let artisanLocations = artisans.map(({ data: { location } }) => location).filter(Boolean)
  artisanLocations = uniq(artisanLocations).sort()
  artisanLocations.map((location) => countryOptions.push({ value: location, label: location }))

  if (country) {
    artisans = artisans.filter(({ data: { location } }) => {
      return country === location
    })
  }

  return {
    props: {
      document,
      artisans: artisans.map((artisan) => prismicArtisanToArtisanCardProps(artisan, { filtered })),
      countryOptions,
    },
  }
}
